import { createContext, useContext, useEffect, useState, useRef, useCallback, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import type { Json } from '@/integrations/supabase/types';
import { rotateSessionId, getSessionId } from '@/core/hooks/session-id';
import {
  AuthState,
  GatingReason,
  BootstrapResponse,
  AUTH_BOOT_TIMEOUT_MS,
  AUTH_LOOP_THRESHOLD,
  AUTH_LOOP_WINDOW_MS,
  deriveGatingReason,
} from './auth-state';
import { buildCallbackUrl } from './redirect-utils';
import { classifyBootstrapError, BootstrapErrorCode } from './gate-state';

// -----------------------------------------------------------------------------
// TYPES
// -----------------------------------------------------------------------------

type UserRole = 'radiologist' | 'admin' | null;

interface AuthContextType {
  // State machine
  authState: AuthState;
  gatingReason: GatingReason | null;

  // Core auth
  user: User | null;
  session: Session | null;
  role: UserRole;
  isAdmin: boolean;

  // Bootstrap data (server-truth)
  bootstrap: BootstrapResponse | null;

  // Derived
  loading: boolean; // Convenience: authState === 'BOOTING'
  canAccessClinical: boolean;

  // Error state
  error: string | null;

  // Actions
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshBootstrap: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// -----------------------------------------------------------------------------
// AUTH EVENT LOGGER (No PHI)
// -----------------------------------------------------------------------------

type AuthEventType =
  | 'AUTH_BOOT_START'
  | 'AUTH_BOOT_SESSION_PRESENT'
  | 'AUTH_BOOT_NO_SESSION'
  | 'AUTH_BOOT_TIMEOUT'
  | 'AUTH_CALLBACK_START'
  | 'AUTH_CALLBACK_EXCHANGED'
  | 'AUTH_CALLBACK_FAILED'
  | 'AUTH_STATE_CHANGE'
  | 'AUTH_LOGIN'
  | 'AUTH_LOGOUT'
  | 'GATE_CHECK_START'
  | 'GATE_RESULT'
  | 'AUTH_LOOP_DETECTED'
  | 'AUTH_STUCK_TIMEOUT'
  | 'AUTH_REFRESH_FAILED'
  | 'AUTH_ERROR'
  | 'BOOTSTRAP_ERROR'; // ADR-0004: Explicit bootstrap failure event

interface AuthEventPayload {
  auth_state?: AuthState;
  gating_reason?: GatingReason | null;
  platform?: 'mobile' | 'desktop';
  route?: string;
  error_code?: string;
  bootstrap_error_code?: BootstrapErrorCode; // ADR-0004: Explicit error classification
  duration_ms?: number;
  [key: string]: unknown;
}

async function logAuthEvent(
  eventType: AuthEventType,
  userId: string | null,
  payload: AuthEventPayload = {}
): Promise<void> {
  try {
    // Determine platform hint
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const platform = isMobile ? 'mobile' : 'desktop';

    // Use ERROR event type for error-related events, AUTH_LOGIN/AUTH_LOGOUT for others
    let dbEventType: 'AUTH_LOGIN' | 'AUTH_LOGOUT' | 'ERROR' = 'AUTH_LOGIN';
    if (eventType === 'AUTH_LOGOUT') {
      dbEventType = 'AUTH_LOGOUT';
    } else if (
      eventType.includes('FAILED') ||
      eventType.includes('TIMEOUT') ||
      eventType.includes('ERROR') ||
      eventType.includes('LOOP')
    ) {
      dbEventType = 'ERROR';
    }

    const eventData = {
      user_id: userId,
      session_id: getSessionId(),
      event_name: dbEventType,
      context: { page: 'AUTH', auth_event_type: eventType } as Json,
      payload: {
        ...payload,
        platform,
        route: typeof window !== 'undefined' ? window.location.pathname : undefined,
      } as Json,
    };

    // Only insert if we have a user_id (some events happen before auth)
    // Note: events table may not exist in all environments - silently skip if missing
    if (userId) {
      const { error } = await supabase.from('events').insert([eventData]);
      // Only log in dev, and suppress "relation does not exist" errors (table may not exist)
      if (error && import.meta.env.DEV && !error.message?.includes('does not exist')) {
        console.warn('[Auth] Failed to log auth event:', eventType, error);
      }
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[Auth] Failed to log auth event:', eventType, err);
    }
  }
}

// -----------------------------------------------------------------------------
// BOOTSTRAP FETCHER
// -----------------------------------------------------------------------------

interface BootstrapFetchResult {
  data: BootstrapResponse | null;
  error: BootstrapErrorCode | null;
  status: number | null;
  message: string | null;
}

// -----------------------------------------------------------------------------
// BOOTSTRAP SESSION CACHE (stale-while-revalidate)
// -----------------------------------------------------------------------------
// A successful bootstrap is cached per-user for the browser session. On the next
// full page load the cached result is applied immediately — the portal renders
// with no BOOTING spinner — and the real bootstrap revalidates in the
// background, updating state (and the cache) when it lands. Cleared on sign-out.

const BOOTSTRAP_CACHE_PREFIX = 'rl_bootstrap_v1:';

function readBootstrapCache(userId: string): BootstrapResponse | null {
  try {
    const raw = sessionStorage.getItem(BOOTSTRAP_CACHE_PREFIX + userId);
    return raw ? (JSON.parse(raw) as BootstrapResponse) : null;
  } catch {
    return null;
  }
}

function writeBootstrapCache(userId: string, data: BootstrapResponse): void {
  try {
    sessionStorage.setItem(BOOTSTRAP_CACHE_PREFIX + userId, JSON.stringify(data));
  } catch {
    /* quota exceeded / private mode — cache is best-effort */
  }
}

function clearBootstrapCache(): void {
  try {
    for (let i = sessionStorage.length - 1; i >= 0; i--) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(BOOTSTRAP_CACHE_PREFIX)) sessionStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}

async function fetchBootstrap(accessToken: string): Promise<BootstrapFetchResult> {
  // DEBUG: Log token presence and length (never log actual token)
  console.log('[Auth] Bootstrap fetch starting', {
    hasToken: !!accessToken,
    tokenLength: accessToken?.length || 0,
    tokenPrefix: accessToken ? accessToken.substring(0, 10) + '...' : 'MISSING',
  });

  if (!accessToken || accessToken.length < 50) {
    console.error('[Auth] Invalid access token for bootstrap', {
      length: accessToken?.length || 0,
      isEmpty: !accessToken,
    });
    return {
      data: null,
      error: 'BOOTSTRAP_401_UNAUTHORIZED',
      status: 401,
      message: 'Invalid or missing access token',
    };
  }

  try {
    const response = await supabase.functions.invoke('session-bootstrap', {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    if (response.error) {
      // Extract status code from FunctionsHttpError if available
      const status = (response.error as { status?: number })?.status || null;
      const message = response.error.message || 'Unknown error';

      // DEBUG: Log full error details
      console.error('[Auth] Bootstrap response error', {
        status,
        message,
        errorType: response.error?.name,
        fullError: JSON.stringify(response.error, null, 2),
      });

      // Classify the error
      let errorCode: BootstrapErrorCode;
      if (status === 401) {
        errorCode = 'BOOTSTRAP_401_UNAUTHORIZED';
      } else if (status === 403) {
        errorCode = 'BOOTSTRAP_403_FORBIDDEN';
      } else if (status && status >= 500) {
        errorCode = 'BOOTSTRAP_5XX';
      } else if (message.includes('Failed to fetch') || message.includes('NetworkError')) {
        errorCode = 'BOOTSTRAP_NETWORK';
      } else if (message.includes('CORS')) {
        errorCode = 'BOOTSTRAP_CORS_BLOCKED';
      } else {
        errorCode = 'BOOTSTRAP_NETWORK';
      }

      console.error(`[Auth] Bootstrap fetch error: ${errorCode} (status=${status})`, message);
      return { data: null, error: errorCode, status, message };
    }

    return { data: response.data as BootstrapResponse, error: null, status: 200, message: null };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Unknown exception';
    console.error('[Auth] Bootstrap fetch exception:', message);

    // Classify exception
    let errorCode: BootstrapErrorCode = 'BOOTSTRAP_NETWORK';
    if (message.includes('Failed to fetch')) {
      errorCode = 'BOOTSTRAP_NETWORK';
    } else if (message.includes('CORS')) {
      errorCode = 'BOOTSTRAP_CORS_BLOCKED';
    }

    return { data: null, error: errorCode, status: null, message };
  }
}

// -----------------------------------------------------------------------------
// LEGACY ROLE FETCHER (fallback if bootstrap fails)
// -----------------------------------------------------------------------------

async function fetchUserRole(userId: string): Promise<UserRole> {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId);

    if (error) {
      console.error('Authentication error occurred');
      return null;
    }

    const roles = data?.map(r => r.role) || [];
    if (roles.includes('admin')) return 'admin';
    return (roles[0] as UserRole) || null;
  } catch {
    console.error('Authentication error occurred');
    return null;
  }
}

async function checkIsAdmin(userId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', 'admin')
      .maybeSingle();

    if (error) {
      console.error('Admin check error occurred');
      return false;
    }
    return !!data;
  } catch {
    console.error('Admin check error occurred');
    return false;
  }
}

// -----------------------------------------------------------------------------
// AUTH PROVIDER
// -----------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  // State machine
  const [authState, setAuthState] = useState<AuthState>('BOOTING');
  const [gatingReason, setGatingReason] = useState<GatingReason | null>(null);

  // Core auth state
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<UserRole>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Bootstrap data
  const [bootstrap, setBootstrap] = useState<BootstrapResponse | null>(null);

  // Error state
  const [error, setError] = useState<string | null>(null);

  // Refs for timeout and loop detection
  const bootTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stateChangeTimestamps = useRef<number[]>([]);
  const mountedRef = useRef(true);

  // Clear error
  const clearError = useCallback(() => setError(null), []);

  // Refresh bootstrap data
  const refreshBootstrap = useCallback(async () => {
    if (!session?.access_token) return;

    logAuthEvent('GATE_CHECK_START', user?.id || null);
    const startTime = Date.now();

    const result = await fetchBootstrap(session.access_token);
    const duration = Date.now() - startTime;

    if (!mountedRef.current) return;

    if (result.data) {
      setBootstrap(result.data);
      setRole(result.data.role);
      setIsAdmin(result.data.is_admin);

      // Determine auth state from bootstrap
      if (result.data.can_access_clinical) {
        setAuthState('AUTHENTICATED');
        setGatingReason(null);
      } else {
        setAuthState('GATED');
        setGatingReason(result.data.gating_reason || 'PENDING_APPROVAL');
      }

      logAuthEvent('GATE_RESULT', user?.id || null, {
        auth_state: result.data.can_access_clinical ? 'AUTHENTICATED' : 'GATED',
        gating_reason: result.data.gating_reason,
        duration_ms: duration,
      });
    } else {
      // Bootstrap failed - use the classified error code
      console.warn('[Auth] Bootstrap failed, falling back to legacy role fetch');
      logAuthEvent('BOOTSTRAP_ERROR', user?.id || null, {
        bootstrap_error_code: result.error,
        duration_ms: duration,
        reason: 'refresh_bootstrap_failed',
        http_status: result.status,
      });
      if (user) {
        const [userRole, adminStatus] = await Promise.all([
          fetchUserRole(user.id),
          checkIsAdmin(user.id),
        ]);
        if (mountedRef.current) {
          setRole(userRole);
          setIsAdmin(adminStatus);
          // Without bootstrap, we can't determine gating accurately
          // Default to AUTHENTICATED and let OnboardingGate handle it
          setAuthState('AUTHENTICATED');
        }
      }
    }
  }, [session?.access_token, user]);

  // Loop detection
  const checkForLoop = useCallback(() => {
    const now = Date.now();
    stateChangeTimestamps.current.push(now);

    // Keep only timestamps within the window
    stateChangeTimestamps.current = stateChangeTimestamps.current.filter(
      ts => now - ts < AUTH_LOOP_WINDOW_MS
    );

    if (stateChangeTimestamps.current.length >= AUTH_LOOP_THRESHOLD) {
      console.error('[Auth] Loop detected! Breaking loop.');
      logAuthEvent('AUTH_LOOP_DETECTED', user?.id || null, {
        changes_count: stateChangeTimestamps.current.length,
      });
      setAuthState('ERROR');
      setError('Authentication loop detected. Please clear your browser data and try again.');
      return true;
    }
    return false;
  }, [user?.id]);

  // Process session - called when session is established or changed
  const processSession = useCallback(async (newSession: Session | null) => {
    if (checkForLoop()) return;

    // Clear boot timeout
    if (bootTimeoutRef.current) {
      clearTimeout(bootTimeoutRef.current);
      bootTimeoutRef.current = null;
    }

    if (!newSession) {
      // No session
      if (mountedRef.current) {
        setSession(null);
        setUser(null);
        setRole(null);
        setIsAdmin(false);
        setBootstrap(null);
        setAuthState('UNAUTHENTICATED');
        setGatingReason(null);
      }
      logAuthEvent('AUTH_BOOT_NO_SESSION', null);
      return;
    }

    // Session exists
    if (mountedRef.current) {
      setSession(newSession);
      setUser(newSession.user);
    }

    logAuthEvent('AUTH_BOOT_SESSION_PRESENT', newSession.user.id);

    // Email verification gate: if email not confirmed, block access
    if (!newSession.user.email_confirmed_at) {
      if (mountedRef.current) {
        setAuthState('GATED');
        setGatingReason('UNVERIFIED');
      }
      logAuthEvent('GATE_RESULT', newSession.user.id, {
        auth_state: 'GATED',
        gating_reason: 'UNVERIFIED',
      });
      return;
    }

    // Stale-while-revalidate: if we have a cached bootstrap for this user,
    // apply it NOW so the portal renders instantly instead of holding the
    // BOOTING spinner, then let the fresh fetch below revalidate.
    const cachedBootstrap = readBootstrapCache(newSession.user.id);
    if (cachedBootstrap && mountedRef.current) {
      setBootstrap(cachedBootstrap);
      setRole(cachedBootstrap.role);
      setIsAdmin(cachedBootstrap.is_admin);
      if (cachedBootstrap.can_access_clinical) {
        setAuthState('AUTHENTICATED');
        setGatingReason(null);
      } else {
        setAuthState('GATED');
        setGatingReason(cachedBootstrap.gating_reason || 'PENDING_APPROVAL');
      }
    }

    // Fetch bootstrap data with timeout protection.
    // 2.5s per attempt + one retry beats the old single 8s race: typical cold
    // edge-function starts land well under 2.5s, and a transient failure gets
    // a second chance before we fall back.
    const BOOTSTRAP_TIMEOUT_MS = 2500;
    const BOOTSTRAP_ATTEMPTS = 2;
    let bootstrapResult: BootstrapFetchResult | null = null;

    try {
      for (let attempt = 1; attempt <= BOOTSTRAP_ATTEMPTS; attempt++) {
        const bootstrapPromise = fetchBootstrap(newSession.access_token);
        const timeoutPromise = new Promise<BootstrapFetchResult>((resolve) =>
          setTimeout(() => resolve({
            data: null,
            error: 'BOOTSTRAP_TIMEOUT',
            status: null,
            message: 'Bootstrap timeout'
          }), BOOTSTRAP_TIMEOUT_MS)
        );

        bootstrapResult = await Promise.race([bootstrapPromise, timeoutPromise]);
        if (bootstrapResult.data || !mountedRef.current) break;
        // Auth failures won't heal on retry — bail to the gate logic below.
        if (bootstrapResult.error === 'BOOTSTRAP_401_UNAUTHORIZED' || bootstrapResult.error === 'BOOTSTRAP_403_FORBIDDEN') break;
      }

      if (bootstrapResult && bootstrapResult.error) {
        console.warn(`[Auth] Bootstrap failed: ${bootstrapResult.error} (status=${bootstrapResult.status})`);
        logAuthEvent('BOOTSTRAP_ERROR', newSession.user.id, {
          duration_ms: BOOTSTRAP_TIMEOUT_MS,
          bootstrap_error_code: bootstrapResult.error,
          http_status: bootstrapResult.status,
          reason: bootstrapResult.error === 'BOOTSTRAP_TIMEOUT' ? 'bootstrap_timeout' : 'bootstrap_failed',
        });
      }
    } catch (err) {
      const errorCode = classifyBootstrapError(null, 'network');
      console.error('[Auth] Bootstrap fetch error:', err);
      logAuthEvent('BOOTSTRAP_ERROR', newSession.user.id, {
        error_code: 'BOOTSTRAP_EXCEPTION',
        bootstrap_error_code: errorCode,
      });
      bootstrapResult = { data: null, error: errorCode, status: null, message: String(err) };
    }

    if (!mountedRef.current) return;

    if (bootstrapResult?.data) {
      writeBootstrapCache(newSession.user.id, bootstrapResult.data);
      setBootstrap(bootstrapResult.data);
      setRole(bootstrapResult.data.role);
      setIsAdmin(bootstrapResult.data.is_admin);

      if (bootstrapResult.data.can_access_clinical) {
        setAuthState('AUTHENTICATED');
        setGatingReason(null);
      } else {
        setAuthState('GATED');
        setGatingReason(bootstrapResult.data.gating_reason || 'PENDING_APPROVAL');
      }

      logAuthEvent('GATE_RESULT', newSession.user.id, {
        auth_state: bootstrapResult.data.can_access_clinical ? 'AUTHENTICATED' : 'GATED',
        gating_reason: bootstrapResult.data.gating_reason,
      });
    } else if (cachedBootstrap) {
      // Fresh bootstrap failed but a cached session state is already applied —
      // keep it rather than downgrading through the legacy fallback.
      console.warn('[Auth] Bootstrap revalidation failed; keeping cached session state');
    } else {
      // Bootstrap failed/timeout - fall back to legacy role fetch with its own timeout
      console.warn('[Auth] Bootstrap unavailable, using legacy fallback');
      
      try {
        const legacyTimeout = 5000;
        const [userRole, adminStatus] = await Promise.race([
          Promise.all([
            fetchUserRole(newSession.user.id),
            checkIsAdmin(newSession.user.id),
          ]),
          new Promise<[UserRole, boolean]>((resolve) => 
            setTimeout(() => resolve([null, false]), legacyTimeout)
          ),
        ]);

        if (mountedRef.current) {
          setRole(userRole);
          setIsAdmin(adminStatus);
          // TEMPORARY: Default to AUTHENTICATED when bootstrap fails
          // This allows users to access the app even if edge function has issues
          // TODO: Revert to GATED/ONBOARDING_REQUIRED when bootstrap is stable
          setAuthState('AUTHENTICATED');
          setGatingReason(null);
        }
      } catch (legacyErr) {
        console.error('[Auth] Legacy role fetch failed:', legacyErr);
        if (mountedRef.current) {
          setAuthState('ERROR');
          setError('Unable to verify access. Please try again.');
        }
      }
    }
  }, [checkForLoop]);

  // Initialize auth on mount
  useEffect(() => {
    mountedRef.current = true;
    logAuthEvent('AUTH_BOOT_START', null);

    // Set boot timeout
    bootTimeoutRef.current = setTimeout(() => {
      if (mountedRef.current && authState === 'BOOTING') {
        console.error('[Auth] Boot timeout exceeded');
        logAuthEvent('AUTH_BOOT_TIMEOUT', null);
        setAuthState('ERROR');
        setError('Authentication timed out. Please try refreshing the page.');
      }
    }, AUTH_BOOT_TIMEOUT_MS);

    // Check for auth callback in URL (PKCE flow)
    const url = new URL(window.location.href);
    const hasAuthParams = url.searchParams.has('code') || url.hash.includes('access_token');

    if (hasAuthParams) {
      logAuthEvent('AUTH_CALLBACK_START', null);

      // Handle PKCE callback
      supabase.auth.exchangeCodeForSession(window.location.href).then(({ data, error: exchangeError }) => {
        if (exchangeError) {
          console.error('[Auth] Callback exchange failed:', exchangeError);
          logAuthEvent('AUTH_CALLBACK_FAILED', null, { error_code: exchangeError.message });
          setError('Authentication callback failed. Please try signing in again.');
          setAuthState('ERROR');
        } else if (data.session) {
          logAuthEvent('AUTH_CALLBACK_EXCHANGED', data.session.user.id);
          // Clean URL params
          window.history.replaceState({}, document.title, window.location.pathname);
          processSession(data.session);
        }
      });
    } else {
      // Normal boot - check for existing session
      supabase.auth.getSession().then(({ data: { session: existingSession } }) => {
        if (mountedRef.current) {
          processSession(existingSession);
        }
      });
    }

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        logAuthEvent('AUTH_STATE_CHANGE', newSession?.user?.id || null, {
          event_type: event,
        });

        // Handle specific events
        if (event === 'SIGNED_OUT') {
          clearBootstrapCache();
          if (mountedRef.current) {
            setSession(null);
            setUser(null);
            setRole(null);
            setIsAdmin(false);
            setBootstrap(null);
            setAuthState('UNAUTHENTICATED');
            setGatingReason(null);
          }
        } else if (event === 'TOKEN_REFRESHED' && newSession) {
          // Token refreshed - update session but don't re-bootstrap
          if (mountedRef.current) {
            setSession(newSession);
          }
        } else if (event === 'SIGNED_IN' && newSession) {
          // New sign in - process session
          processSession(newSession);
        }
      }
    );

    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
      if (bootTimeoutRef.current) {
        clearTimeout(bootTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally only run on mount - auth initialization should happen once

  // Sign in
  const signIn = useCallback(async (email: string, password: string) => {
    const { data, error: signInError } = await supabase.auth.signInWithPassword({
      email: email.trim().toLowerCase(),
      password,
    });

    if (!signInError && data.user) {
      rotateSessionId();
      logAuthEvent('AUTH_LOGIN', data.user.id);
    }

    return { error: signInError as Error | null };
  }, []);

  // Sign up
  const signUp = useCallback(async (email: string, password: string, fullName: string) => {
    const { error: signUpError } = await supabase.auth.signUp({
      email: email.trim().toLowerCase(),
      password,
      options: {
        emailRedirectTo: buildCallbackUrl(),
        data: { full_name: fullName },
      },
    });

    return { error: signUpError as Error | null };
  }, []);

  // Sign out
  const signOut = useCallback(async () => {
    if (user) {
      await logAuthEvent('AUTH_LOGOUT', user.id);
    }
    await supabase.auth.signOut();
    clearBootstrapCache();
    setUser(null);
    setSession(null);
    setRole(null);
    setIsAdmin(false);
    setBootstrap(null);
    setAuthState('UNAUTHENTICATED');
    setGatingReason(null);
  }, [user]);

  // Derived values
  const loading = authState === 'BOOTING';
  const canAccessClinical = bootstrap?.can_access_clinical ?? false;

  return (
    <AuthContext.Provider
      value={{
        authState,
        gatingReason,
        user,
        session,
        role,
        isAdmin,
        bootstrap,
        loading,
        canAccessClinical,
        error,
        signIn,
        signUp,
        signOut,
        refreshBootstrap,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
